import { Box } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLPaper } from "OLPaper";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useSettings } from "api/general";
import { FormButtons } from "components/FormControls/FormButtons";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate } from "react-router-dom";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import * as Yup from "yup";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    OLPaper: {
      display: "flex",
      flexDirection: "column",
      marginTop: theme.spacing(8),
    },
    itemForm: { "& + &": { marginTop: theme.spacing(10) } },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      marginTop: theme.spacing(5),
      "& + &": {
        marginTop: 0,
      },
    },
  }),
);

export function ChangePassword() {
  const classes = useStyles();
  const { notify } = useNotifications();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { changePassword } = useSettings();
  return (
    <>
      <TopHeader title={message({ id: "changePassword" })} />
      <Formik
        enableReinitialize
        initialStatus={{}}
        initialValues={{
          currentPassword: "",
          plainPassword: "",
          plainPassword2: "",
        }}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});
          try {
            const { plainPassword2, ...formValues } = values;
            const response = await changePassword(formValues);
            if (response) {
              navigate(links.homepage);
              notify(message({ id: "settingsSaved" }));
            }
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            focusError();
          }
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          plainPassword: Yup.string()
            .oneOf(
              [Yup.ref("plainPassword2"), undefined],
              message({ id: "passwordsNotEqualError" }),
            )
            .required(message({ id: "requiredFieldError" })),
          plainPassword2: Yup.string()
            .oneOf(
              [Yup.ref("plainPassword"), undefined],
              message({ id: "passwordsNotEqualError" }),
            )
            .required(message({ id: "requiredFieldError" })),
        })}
      >
        {({
          handleSubmit,
          isSubmitting,
          status,
          values,
          dirty,
          initialValues,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              {status?.[""] && (
                <span className={classes.error}>{status[""]}</span>
              )}
              <OLPaper className={classes.OLPaper}>
                <TextInput
                  className={classes.itemForm}
                  label={
                    <RequiredFieldLabel
                      text={message({ id: "currentPassword" })}
                    />
                  }
                  name="currentPassword"
                  type="password"
                  data-test-id="change-password-current-password-input"
                />
                <TextInput
                  className={classes.itemForm}
                  label={
                    <RequiredFieldLabel text={message({ id: "newPassword" })} />
                  }
                  name="plainPassword"
                  type="password"
                  data-test-id="change-password-new-password-input"
                />
                <TextInput
                  className={classes.itemForm}
                  label={
                    <RequiredFieldLabel
                      text={message({ id: "repeatNewPassword" })}
                    />
                  }
                  name="plainPassword2"
                  type="password"
                  data-test-id="change-password-repeat-new-password-input"
                />
              </OLPaper>
              {!isSubmitting && (
                <LeaveSiteGuard
                  warnBeforeRefresh
                  isDirty={dirty}
                  initialValues={initialValues}
                  currentValues={values}
                />
              )}
              <Box mt={12} />
              <FormButtons
                disableSubmit={isSubmitting}
                onCancelClick={() => navigate(links.dashboard)}
                onSubmitClick={() => handleSubmit()}
              />
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
